'use client';

import { queryOptions, useQuery } from '@tanstack/react-query';
import type { McpAccessResponseDto } from '@microservices/dtos';

import { apiClient } from '@/infrastructure/api/client';

import { PERMISSIONS_QUERY_KEY } from './auth-queries';
import { useAuth } from './useAuth';

/** admin-api-gateway's `GET /platform/mcp-connections/v1.0/access`, through the `/api/admin` BFF proxy. */
export const MCP_CONNECTIONS_ACCESS_PATH = '/platform/mcp-connections/v1.0/access';

/**
 * Nested under the permissions key ON PURPOSE: every place that already
 * invalidates or drops the permissions (login, role switch, logout, a 401 from
 * the BFF) matches by prefix and takes this entry with it — no second list of
 * call sites to keep in sync.
 */
export const MCP_CONNECTIONS_ACCESS_QUERY_KEY = [
  ...PERMISSIONS_QUERY_KEY,
  'mcp-connections-access',
] as const;

export async function fetchMcpConnectionsAccess(): Promise<boolean> {
  const response = await apiClient.get<McpAccessResponseDto>(
    MCP_CONNECTIONS_ACCESS_PATH
  );
  return response.data?.allowed === true;
}

export const mcpConnectionsAccessQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: MCP_CONNECTIONS_ACCESS_QUERY_KEY,
    queryFn: fetchMcpConnectionsAccess,
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: false,
  });

/**
 * Whether the session may open the MCP console (`/mcp-connections`).
 *
 * The one surface gate that is NOT read from the active role's permissions:
 * MCP follows the UNION of every role the user holds (decision 2026-09-29), and
 * only the gateway knows that union, so it answers the question itself. A
 * failed read is a denial — `allowed` is `true` only on an explicit `true`.
 *
 * `isLoading` means "the answer is not final yet", which is wider than
 * react-query's own `isLoading` (`isPending && isFetching`):
 * - the first read is in flight; or
 * - anything but an explicit `true` is being re-read — a stale cached denial
 *   refetching, or a read paused while offline (`isPaused`). React-query
 *   reports neither as loading, and acting on that `false` turned away a
 *   user the gateway was about to allow (AB#28616).
 * A cached `true` being re-read is usable at once. A disabled read (no
 * session) is not loading: there is nothing to wait for.
 */
export function useMcpConnectionsAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  const { isAuthenticated } = useAuth();
  const { data, isLoading, isFetching, isPaused } = useQuery(
    mcpConnectionsAccessQueryOptions(isAuthenticated)
  );
  const allowed = data === true;
  return {
    allowed,
    isLoading: isLoading || ((isFetching || isPaused) && !allowed),
  };
}
